import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * HeaderTooltip — element-anchored, viewport-clamped, mobile-safe tooltip
 * for table column headers and similar trigger elements.
 *
 *   - Anchored to the trigger's bounding rect (not mouse) so it stays
 *     stable on touch screens.
 *   - Measures rendered width/height to clamp against
 *     `document.documentElement.clientWidth` (scrollbar-excluded).
 *   - Prefers above the trigger, flips below when near the top edge.
 *   - Mouse hover shows instantly; touch (`touchstart`) shows then
 *     auto-hides after `autoHideMs` (default 4500ms). Outside-tap
 *     dismisses immediately.
 *   - Single global <TooltipLayer /> per page (or per table) renders the
 *     current bubble; `useAnchoredTip()` returns stateful handlers and
 *     the render component.
 *
 * Usage pattern:
 *   const { handlers, Tooltip } = useAnchoredTip();
 *   ...
 *   <TableHead {...handlers('Explains what this column means.')}>Price</TableHead>
 *   <Tooltip />
 */

export function useAnchoredTip({ autoHideMs = 4500 } = {}) {
  const [state, setState] = useState({ tip: null, anchorRect: null });
  const timer = useRef(null);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setState({ tip: null, anchorRect: null });
  }, []);

  // Resolve the actual element we should anchor against. Walks up via
  // closest() to find the column-header / button / combobox, falling back
  // to currentTarget so this works on any interactive element.
  const resolveEl = useCallback((e) => {
    const t = e?.currentTarget;
    if (!t?.closest) return t;
    return t.closest('th, [role="columnheader"], [data-tip-anchor]') || t;
  }, []);

  const handlers = useCallback((tip) => {
    if (!tip) return {};
    return {
      onMouseEnter: (e) => {
        if (timer.current) clearTimeout(timer.current);
        const el = resolveEl(e);
        const rect = el?.getBoundingClientRect();
        if (rect) setState({ tip, anchorRect: rect });
      },
      onMouseLeave: clear,
      onTouchStart: (e) => {
        if (timer.current) clearTimeout(timer.current);
        const el = resolveEl(e);
        const rect = el?.getBoundingClientRect();
        if (!rect) return;
        setState({ tip, anchorRect: rect });
        timer.current = setTimeout(() => {
          timer.current = null;
          setState({ tip: null, anchorRect: null });
        }, autoHideMs);
      },
    };
  }, [autoHideMs, clear, resolveEl]);

  // Outside-tap dismissal for mobile — without this, the bubble from a
  // previous tap can linger visibly until the auto-hide fires.
  useEffect(() => {
    const onDocPointer = (ev) => {
      const el = ev.target;
      if (el?.closest?.('th, [role="columnheader"], [data-tip-anchor]')) return;
      clear();
    };
    document.addEventListener('touchstart', onDocPointer, { passive: true });
    return () => document.removeEventListener('touchstart', onDocPointer);
  }, [clear]);

  const Tooltip = useCallback(() => <AnchoredTooltip {...state} />, [state]);

  return { handlers, Tooltip, clear };
}

/**
 * AnchoredTooltip — renders the bubble with element-anchored, viewport-
 * clamped positioning. Pre-renders off-screen at opacity 0, then fades in
 * at the measured coordinates to avoid a visible "jump" on first paint.
 */
function AnchoredTooltip({ tip, anchorRect }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!tip || !anchorRect) {
      setPos(null);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const MARGIN = 8;
    const bubbleW = node.offsetWidth || 320;
    const bubbleH = node.offsetHeight || 100;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;

    let top;
    if (anchorRect.top - bubbleH - MARGIN >= 0) {
      top = anchorRect.top - bubbleH - MARGIN;
    } else {
      top = Math.min(anchorRect.bottom + MARGIN, vh - bubbleH - MARGIN);
    }

    let left = anchorRect.left + anchorRect.width / 2 - bubbleW / 2;
    left = Math.max(MARGIN, Math.min(vw - bubbleW - MARGIN, left));
    setPos({ left, top });
  }, [tip, anchorRect]);

  if (!tip || !anchorRect) return null;

  return (
    <div
      ref={ref}
      className="fixed z-[9999] px-3 py-2.5 rounded-lg bg-card border border-border shadow-xl text-xs text-foreground leading-relaxed pointer-events-none"
      style={{
        left: pos ? `${pos.left}px` : '-9999px',
        top: pos ? `${pos.top}px` : '-9999px',
        maxWidth: '320px',
        opacity: pos ? 1 : 0,
        transition: 'opacity 120ms ease',
      }}
      data-testid="anchored-tooltip"
    >
      {tip}
    </div>
  );
}
